import * as React from 'react'
import { Check, ChevronDown, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

type Chapter = { id: number; name: string }

export function ChapterPicker({
  setting,
  chapters,
  selectedIds,
  error,
  onChange,
}: {
  setting?: 'hidden' | 'optional' | 'required'
  chapters: Chapter[]
  selectedIds: number[]
  error?: string
  onChange: (ids: number[]) => void
}) {
  const [open, setOpen] = React.useState(false)
  if (setting === 'hidden' || !setting || chapters.length === 0) return null
  const selected = chapters.filter((chapter) => selectedIds.includes(chapter.id))
  const toggle = (id: number) =>
    onChange(
      selectedIds.includes(id) ? selectedIds.filter((value) => value !== id) : [...selectedIds, id],
    )

  return (
    <section className="space-y-3" aria-labelledby="join-chapter-heading">
      <div>
        <div className="flex items-center gap-2">
          <h2 id="join-chapter-heading" className="text-body font-semibold text-foreground">
            Chapter
          </h2>
          {setting === 'required' && <Badge variant="warning">Required</Badge>}
        </div>
        <p className="mt-1 text-caption text-muted-foreground">Local groups you can join.</p>
        {error && <p className="mt-1 text-caption text-destructive">{error}</p>}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            aria-invalid={Boolean(error)}
            className="h-9 w-full justify-between px-3 font-normal"
          >
            <span className={cn(selected.length === 0 && 'text-muted-foreground')}>
              {selected.length === 0
                ? 'Select chapters'
                : `${selected.length} chapter${selected.length === 1 ? '' : 's'} selected`}
            </span>
            <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search chapters" />
            <CommandList>
              <CommandEmpty>No chapter matches.</CommandEmpty>
              {chapters.map((chapter) => {
                const isSelected = selectedIds.includes(chapter.id)
                return (
                  <CommandItem
                    key={chapter.id}
                    value={chapter.name}
                    onSelect={() => toggle(chapter.id)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <Check
                      className={cn('size-4 text-primary', !isSelected && 'invisible')}
                      aria-hidden="true"
                    />
                    {chapter.name}
                  </CommandItem>
                )
              })}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((chapter) => (
            <Badge key={chapter.id} className="gap-1 bg-primary/10 text-caption text-primary">
              {chapter.name}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="-mr-1 h-4 w-4 p-0 text-primary hover:bg-transparent hover:text-primary/70"
                aria-label={`Remove ${chapter.name}`}
                onClick={() => toggle(chapter.id)}
              >
                <X className="size-3" aria-hidden="true" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </section>
  )
}
